import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import clsx from 'clsx';

const ADMIN_ROLES = ['superadmin', 'dealer_admin', 'branch_admin'];

// Matches NoteDetail's transcript-timeout threshold — a note with no
// transcript past this age is failed, not still processing, and should
// read that way here too instead of "(processing…)" forever.
const TRANSCRIPT_TIMEOUT_MS = 120_000;
function notePreview(n) {
  if (n.structured?.summary) return { text: n.structured.summary, failed: false };
  if (n.transcript) return { text: n.transcript.slice(0, 160), failed: false };
  const timedOut = Date.now() - new Date(n.created_at).getTime() > TRANSCRIPT_TIMEOUT_MS;
  return timedOut
    ? { text: 'Transcription failed — open note to retry', failed: true }
    : { text: '(processing…)', failed: false };
}

export default function Dashboard() {
  const { user } = useAuth();
  if (ADMIN_ROLES.includes(user?.role)) return <AdminDashboard />;
  return <RepDashboard />;
}

// ─── Sales rep view ───────────────────────────────────────────────────────────

function RepDashboard() {
  const { user } = useAuth();
  const followups = useQuery({
    queryKey: ['followups', 'pending'],
    queryFn: () => api.get('/followups?status=pending').then(r => r.data.data),
  });
  const aiq = useQuery({
    queryKey: ['ai-questions', 'pending'],
    queryFn: () => api.get('/ai-questions?status=pending').then(r => r.data.data),
  });
  const recentNotes = useQuery({
    queryKey: ['notes', 'recent'],
    queryFn: () => api.get('/notes').then(r => r.data.data.slice(0, 5)),
  });

  return (
    <div className="space-y-6">
      <header>
        <div className="section-eyebrow">Welcome back</div>
        <h1 className="text-2xl">{user?.full_name?.split(' ')[0] || 'BDM'}</h1>
        <div className="rule-crimson" />
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/notes/new" className="card p-5 hover:shadow-lg transition border-l-4 border-crimson">
          <div className="section-eyebrow">Capture</div>
          <div className="text-xl font-semibold text-navy mt-1">Record a Note</div>
          <p className="text-sm text-muted mt-2">Voice or typed. Structured automatically.</p>
        </Link>
        <Link to="/ai-questions" className="card p-5 hover:shadow-lg transition border-l-4 border-navy">
          <div className="section-eyebrow">AI Inbox</div>
          <div className="text-2xl font-bold text-navy mt-1">{aiq.data?.length ?? 0}</div>
          <p className="text-sm text-muted mt-2">Pending clarifying questions.</p>
        </Link>
        <Link to="/followups" className="card p-5 hover:shadow-lg transition border-l-4 border-gold">
          <div className="section-eyebrow">Follow-ups</div>
          <div className="text-2xl font-bold text-navy mt-1">{followups.data?.length ?? 0}</div>
          <p className="text-sm text-muted mt-2">Pending due.</p>
        </Link>
      </div>

      <section>
        <div className="section-eyebrow">Recent Notes</div>
        <div className="rule-crimson" />
        <div className="space-y-2 mt-2">
          {recentNotes.data?.length === 0 && (
            <div className="card p-4 text-muted text-sm">No notes yet. Tap "Record a Note" above.</div>
          )}
          {recentNotes.data?.map(n => {
            const preview = notePreview(n);
            return (
              <Link key={n.id} to={`/notes/${n.id}`} className="card p-3 block hover:bg-panel">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-navy">
                    {n.customer_name || n.structured?.customer_name_guess || '(no customer)'}
                  </div>
                  <div className="text-xs text-muted">{new Date(n.created_at).toLocaleString()}</div>
                </div>
                <div className={clsx('text-sm mt-1 line-clamp-2', preview.failed ? 'text-crimson font-medium' : 'text-ink')}>
                  {preview.text}
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}

// ─── Admin / manager view ─────────────────────────────────────────────────────

function fmtMoney(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (n >= 1_000_000) return '$' + (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return '$' + Math.round(n / 1_000) + 'k';
  return '$' + Math.round(n);
}

function AdminDashboard() {
  const { user } = useAuth();
  const team = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: () => api.get('/admin/dashboard').then(r => r.data.data),
    staleTime: 2 * 60 * 1000,
  });

  const d = team.data;

  // Aggregate follow-up totals from the urgency breakdown
  const totalPending = d?.followups?.reduce((s, f) => s + Number(f.n), 0) ?? 0;
  const totalOverdue = d?.followups?.reduce((s, f) => s + Number(f.overdue), 0) ?? 0;
  const notesThisWeek = d?.reps?.reduce((s, r) => s + Number(r.notes_week), 0) ?? 0;

  return (
    <div className="space-y-6">
      <header>
        <div className="section-eyebrow">Team Overview</div>
        <h1 className="text-2xl">{user?.full_name?.split(' ')[0] || 'Manager'}</h1>
        <div className="rule-crimson" />
      </header>

      {/* Quick stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Link to="/followups" className="card p-4 hover:shadow-lg transition border-l-4 border-gold">
          <div className="section-eyebrow">Follow-ups</div>
          <div className="text-2xl font-bold text-navy mt-1">
            {team.isLoading ? '…' : totalPending}
          </div>
          <p className="text-xs text-muted mt-1">Pending (all reps)</p>
        </Link>
        <Link to="/admin/stale-followups" className="card p-4 hover:shadow-lg transition border-l-4 border-crimson">
          <div className="section-eyebrow">Overdue</div>
          <div className="text-2xl font-bold text-crimson mt-1">
            {team.isLoading ? '…' : totalOverdue}
          </div>
          <p className="text-xs text-muted mt-1">Past due right now</p>
        </Link>
        <div className="card p-4 border-l-4 border-navy">
          <div className="section-eyebrow">Notes this week</div>
          <div className="text-2xl font-bold text-navy mt-1">
            {team.isLoading ? '…' : notesThisWeek}
          </div>
          <p className="text-xs text-muted mt-1">Across all reps</p>
        </div>
        <Link to="/pipeline" className="card p-4 hover:shadow-lg transition border-l-4 border-navy">
          <div className="section-eyebrow">Pipeline</div>
          <div className="text-2xl font-bold text-navy mt-1">
            {team.isLoading ? '…' : fmtMoney(d?.pipeline?.weighted)}
          </div>
          <p className="text-xs text-muted mt-1">Weighted forecast</p>
        </Link>
      </div>

      {/* Quick nav to admin tools */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Link to="/admin/dashboard" className="card p-4 hover:shadow-lg transition flex items-center gap-3">
          <span className="text-2xl">◫</span>
          <div>
            <div className="font-semibold text-navy">Manager Dashboard</div>
            <div className="text-xs text-muted">Pipeline, rep rollups, aging deals</div>
          </div>
        </Link>
        <Link to="/reports/activity" className="card p-4 hover:shadow-lg transition flex items-center gap-3">
          <span className="text-2xl">📋</span>
          <div>
            <div className="font-semibold text-navy">Rep Activity</div>
            <div className="text-xs text-muted">Touch counts, follow-up rates, trends</div>
          </div>
        </Link>
      </div>

      {/* Rep activity this week */}
      {d?.reps && d.reps.length > 0 && (
        <section className="card p-4">
          <div className="section-eyebrow">Rep Activity — This Week</div>
          <div className="rule-crimson" />
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted uppercase tracking-wider border-b border-navy-100">
                  <th className="pb-2 pr-4">Rep</th>
                  <th className="pb-2 pr-4">Branch</th>
                  <th className="pb-2 pr-3 text-right">Notes</th>
                  <th className="pb-2 pr-3 text-right">Visits</th>
                  <th className="pb-2 pr-3 text-right">Calls</th>
                  <th className="pb-2 text-right">Last note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-50">
                {d.reps.map(r => {
                  const lastAt = r.last_note_at ? new Date(r.last_note_at) : null;
                  const daysSince = lastAt
                    ? Math.floor((Date.now() - lastAt) / 86400000)
                    : null;
                  return (
                    <tr key={r.id} className="hover:bg-panel">
                      <td className="py-2 pr-4">
                        <Link to={`/admin/reps/${r.id}`} className="text-navy hover:underline font-medium">
                          {r.full_name}
                        </Link>
                      </td>
                      <td className="py-2 pr-4 text-muted text-xs">{r.branch_name || '—'}</td>
                      <td className={clsx('py-2 pr-3 text-right font-semibold',
                        Number(r.notes_week) === 0 ? 'text-crimson' : 'text-navy')}>
                        {r.notes_week}
                      </td>
                      <td className="py-2 pr-3 text-right text-muted">{r.visits_week}</td>
                      <td className="py-2 pr-3 text-right text-muted">{r.calls_week}</td>
                      <td className="py-2 text-right text-xs text-muted">
                        {daysSince == null ? 'never'
                          : daysSince === 0 ? 'today'
                          : daysSince === 1 ? 'yesterday'
                          : `${daysSince}d ago`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* High-alert notes from last 14 days */}
      {d?.alert_notes && d.alert_notes.length > 0 && (
        <section className="card p-4">
          <div className="section-eyebrow">High-Alert Notes (last 14 days)</div>
          <div className="rule-crimson" />
          <div className="mt-3 space-y-2">
            {d.alert_notes.slice(0, 5).map(n => (
              <Link key={n.id} to={`/notes/${n.id}`}
                className="block bg-panel rounded p-3 hover:bg-crimson/5 transition">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-navy line-clamp-2">{n.summary}</div>
                    <div className="text-xs text-muted mt-1">
                      {n.customer_name && <span className="mr-2">{n.customer_name}</span>}
                      {n.user_name && <span className="opacity-70">· {n.user_name}</span>}
                    </div>
                  </div>
                  <div className="text-xs text-muted shrink-0">
                    {new Date(n.created_at).toLocaleDateString()}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {team.isLoading && (
        <div className="text-muted text-sm">Loading team data…</div>
      )}
    </div>
  );
}
